import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { useNavigate, useParams } from 'react-router-dom';
import { DetailPageHeader } from '../../components/layout/DetailPageHeader';
import { ImageUpload } from '../../components/atoms/UploadImage';
import useSnackbar from '../../hooks/useSnackbar';
import { formatDate } from '../../utils/helper';
import {
  getChannelPartnerState,
  getIdentityDocumentViewUrl,
  PartnerDetailState,
  PartnerStatus,
  suspendChannelPartner,
  unsuspendChannelPartner,
  updatePartnerAddress,
  updatePartnerBank,
  updatePartnerBusiness,
  updatePartnerProfile,
} from '../../services/channelPartners';
import {
  BUSINESS_TYPE_OPTIONS,
  PARTNER_TYPE_OPTIONS,
  partnerTypeLabel,
  BusinessFormState,
  AddressFormState,
  BankFormState,
  emptyBusinessForm,
  emptyAddressForm,
  emptyBankForm,
  validateBusinessForm,
  validateAddressForm,
  validateBankForm,
  businessFormToPayload,
  addressFormToPayload,
  bankFormToPayload,
} from './partnerFormFields';

const ACCENT = '#E31E25';
const ACCENT_HOVER = '#C62828';

const statusMeta: Record<PartnerStatus, { label: string; color: string; bg: string }> = {
  DRAFT: { label: 'Draft', color: '#94a3b8', bg: '#64748b22' },
  UNDER_REVIEW: { label: 'Under review', color: '#f59e0b', bg: '#f59e0b22' },
  APPROVED: { label: 'Approved', color: '#10b981', bg: '#10b98122' },
  REJECTED: { label: 'Rejected', color: '#ef4444', bg: '#ef444422' },
  SUSPENDED: { label: 'Suspended', color: '#a855f7', bg: '#a855f722' },
};

const pretty = (value?: string | null) =>
  value ? value.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'N/A';

const Value = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Typography variant="caption" color="#64748b">
      {label}
    </Typography>
    <Typography color="#e2e8f0" fontSize={14} sx={{ overflowWrap: 'anywhere' }}>
      {value || 'N/A'}
    </Typography>
  </Box>
);

const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  action,
  children,
}) => (
  <Paper sx={{ p: 2, height: '100%', bgcolor: '#171717', border: '1px solid #242424', borderRadius: 2 }}>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
      <Typography color="#fff" fontWeight={700}>
        {title}
      </Typography>
      {action}
    </Box>
    {children}
  </Paper>
);

const fieldSx = {
  '& .MuiInputBase-root': { bgcolor: '#111', color: '#e2e8f0', borderRadius: '8px' },
  '& .MuiInputLabel-root': { color: '#64748b' },
};

const StatusChip = ({ status }: { status: PartnerStatus }) => {
  const meta = statusMeta[status] || statusMeta.DRAFT;
  return <Chip label={meta.label} size="small" sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600 }} />;
};

const CheckChip = ({ result }: { result: string }) => {
  const color =
    result === 'PASSED' ? '#10b981' : result === 'FAILED' ? '#ef4444' : result === 'MANUAL_REVIEW' ? '#f59e0b' : '#94a3b8';
  return <Chip label={pretty(result)} size="small" sx={{ bgcolor: `${color}22`, color, fontWeight: 600 }} />;
};

const SectionSaveActions = ({
  onSave,
  onCancel,
  saving,
}: {
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) => (
  <Box display="flex" gap={1} mt={2}>
    <Button
      size="small"
      variant="contained"
      onClick={onSave}
      disabled={saving}
      sx={{ backgroundColor: ACCENT, '&:hover': { backgroundColor: ACCENT_HOVER }, textTransform: 'none' }}
    >
      {saving ? 'Saving…' : 'Save'}
    </Button>
    <Button size="small" onClick={onCancel} disabled={saving} sx={{ color: '#94a3b8', textTransform: 'none' }}>
      Cancel
    </Button>
  </Box>
);

const PartnerDetail = () => {
  const { id: partnerId } = useParams();
  const navigate = useNavigate();
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const [state, setState] = useState<PartnerDetailState | null>(null);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState<'suspend' | 'unsuspend' | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchState = useCallback(async () => {
    if (!partnerId) return;
    try {
      setLoading(true);
      setState(await getChannelPartnerState(partnerId));
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to load partner', 'error');
    } finally {
      setLoading(false);
    }
    // showSnackbar's identity changes every render (useSnackbar doesn't
    // memoize it) — including it here would re-create fetchState on every
    // render, re-firing the effect below in an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  const identityNeedsReview = useMemo(
    () => state?.checks.some((check) => check.checkType === 'IDENTITY' && check.result === 'MANUAL_REVIEW'),
    [state],
  );

  const submitAction = async () => {
    if (!partnerId || !action) return;
    try {
      setSaving(true);
      if (action === 'suspend') await suspendChannelPartner(partnerId);
      else await unsuspendChannelPartner(partnerId);
      showSnackbar(action === 'suspend' ? 'Partner suspended' : 'Partner reactivated', 'success');
      setAction(null);
      await fetchState();
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to update partner status', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Section editing (Contact / Firm / Address / Bank) ───────────────────
  type EditableSection = 'contact' | 'firm' | 'address' | 'bank';
  const [editingSection, setEditingSection] = useState<EditableSection | null>(null);
  const [sectionSaving, setSectionSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  const [contactForm, setContactForm] = useState({
    fullName: '',
    email: '',
    dateOfBirth: '',
    partnerType: '',
    profilePhoto: '',
  });
  const [businessForm, setBusinessForm] = useState<BusinessFormState>(emptyBusinessForm);
  const [addressForm, setAddressForm] = useState<AddressFormState>(emptyAddressForm);
  const [bankForm, setBankForm] = useState<BankFormState>(emptyBankForm);

  const startEdit = (section: EditableSection) => {
    if (!state) return;
    if ((section === 'address' || section === 'bank') && !state.firm) {
      showSnackbar('Save business details first — address and bank details are attached to the firm', 'error');
      return;
    }
    if (section === 'contact') {
      setContactForm({
        fullName: state.partner.fullName || '',
        email: state.partner.email || '',
        dateOfBirth: state.partner.dateOfBirth ? String(state.partner.dateOfBirth).slice(0, 10) : '',
        partnerType: state.partner.partnerType || '',
        profilePhoto: state.partner.profilePhoto || '',
      });
    } else if (section === 'firm') {
      setBusinessForm({
        businessType: (state.firm?.businessType as BusinessFormState['businessType']) || '',
        legalName: state.firm?.legalName || '',
        tradingName: state.firm?.tradingName || '',
        cin: state.firm?.cin || '',
        pan: '',
        gstin: state.firm?.gstin || '',
      });
    } else if (section === 'address') {
      setAddressForm({
        pincode: state.address?.pincode || '',
        line1: state.address?.line1 || '',
        line2: state.address?.line2 || '',
        city: state.address?.city || '',
        state: state.address?.state || '',
      });
    } else if (section === 'bank') {
      setBankForm({
        accountHolderName: state.bank?.accountHolderName || '',
        ifscCode: state.bank?.ifscCode || '',
        accountNumber: '',
      });
    }
    setEditingSection(section);
  };

  const cancelEdit = () => setEditingSection(null);

  const saveContact = async () => {
    if (!partnerId) return;
    if (!contactForm.fullName.trim()) return showSnackbar('Full name is required', 'error');
    if (!contactForm.dateOfBirth) return showSnackbar('Date of birth is required', 'error');
    try {
      setSectionSaving(true);
      await updatePartnerProfile(partnerId, {
        fullName: contactForm.fullName.trim(),
        email: contactForm.email.trim() || undefined,
        dateOfBirth: contactForm.dateOfBirth,
        partnerType: contactForm.partnerType || undefined,
        profilePhoto: contactForm.profilePhoto,
      });
      showSnackbar('Contact details updated', 'success');
      setEditingSection(null);
      await fetchState();
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to update contact details', 'error');
    } finally {
      setSectionSaving(false);
    }
  };

  const saveFirm = async () => {
    if (!partnerId) return;
    const validationError = validateBusinessForm(businessForm);
    if (validationError) return showSnackbar(validationError, 'error');
    try {
      setSectionSaving(true);
      await updatePartnerBusiness(partnerId, businessFormToPayload(businessForm));
      showSnackbar('Business details updated', 'success');
      setEditingSection(null);
      await fetchState();
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to update business details', 'error');
    } finally {
      setSectionSaving(false);
    }
  };

  const saveAddress = async () => {
    if (!partnerId) return;
    const validationError = validateAddressForm(addressForm);
    if (validationError) return showSnackbar(validationError, 'error');
    try {
      setSectionSaving(true);
      await updatePartnerAddress(partnerId, addressFormToPayload(addressForm));
      showSnackbar('Address updated', 'success');
      setEditingSection(null);
      await fetchState();
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to update address', 'error');
    } finally {
      setSectionSaving(false);
    }
  };

  const saveBank = async () => {
    if (!partnerId) return;
    const validationError = validateBankForm(bankForm);
    if (validationError) return showSnackbar(validationError, 'error');
    try {
      setSectionSaving(true);
      await updatePartnerBank(partnerId, bankFormToPayload(bankForm));
      showSnackbar('Bank details updated', 'success');
      setEditingSection(null);
      await fetchState();
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to update bank details', 'error');
    } finally {
      setSectionSaving(false);
    }
  };

  const EditAction = ({ section }: { section: EditableSection }) => (
    <IconButton size="small" onClick={() => startEdit(section)} sx={{ color: '#94a3b8' }}>
      <EditIcon fontSize="small" />
    </IconButton>
  );

  const handleViewDocument = async () => {
    if (!state?.identityDocument?.id) return;
    try {
      const { signedUrl } = await getIdentityDocumentViewUrl(state.identityDocument.id);
      window.open(signedUrl, '_blank');
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to load document', 'error');
    }
  };

  const partner = state?.partner;
  const status = (state?.derivedStatus as PartnerStatus) || partner?.status || 'DRAFT';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '100%' }}>
      {SnackbarComponent}
      <DetailPageHeader
        title="Channel Partner Details"
        actions={
          <Box display="flex" gap={1} flexWrap="wrap">
            {identityNeedsReview && (
              <Button
                variant="outlined"
                startIcon={<FactCheckIcon />}
                sx={{ color: ACCENT, borderColor: ACCENT }}
                onClick={() => navigate('/channel-partners/identity')}
              >
                Review Identity
              </Button>
            )}
            {partner && partner.status !== 'DRAFT' && partner.status !== 'SUSPENDED' && (
              <Button color="error" variant="outlined" startIcon={<BlockIcon />} onClick={() => setAction('suspend')}>
                Suspend
              </Button>
            )}
            {partner?.status === 'SUSPENDED' && (
              <Button color="success" variant="contained" startIcon={<CheckCircleOutlineIcon />} onClick={() => setAction('unsuspend')}>
                Unsuspend
              </Button>
            )}
          </Box>
        }
      />

      <Box sx={{ p: 2, overflowY: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {loading ? (
          <Paper sx={{ p: 2, bgcolor: '#171717', color: '#94a3b8' }}>Loading partner...</Paper>
        ) : !state || !partner ? (
          <Paper sx={{ p: 2, bgcolor: '#171717', color: '#94a3b8' }}>Partner not found</Paper>
        ) : (
          <>
            <Paper sx={{ p: 2, bgcolor: '#171717', border: '1px solid #242424', borderRadius: 2 }}>
              <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                <Box display="flex" gap={2} alignItems="center">
                  <Avatar src={partner.profilePhoto || undefined} alt={partner.fullName || 'Partner'} sx={{ width: 56, height: 56, bgcolor: ACCENT, fontWeight: 700 }}>
                    {partner.fullName?.charAt(0)?.toUpperCase() || '?'}
                  </Avatar>
                  <Box>
                    <Typography color="#fff" variant="h6" fontWeight={700}>
                      {partner.fullName || 'Unnamed partner'}
                    </Typography>
                    <Typography color="#64748b" fontSize={13}>
                      {partner.email || 'No email'} · {partner.phoneNumber}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <StatusChip status={status} />
                  {partner.status !== status && (
                    <Chip label={`Stored: ${pretty(partner.status)}`} size="small" variant="outlined" sx={{ borderColor: '#333', color: '#94a3b8' }} />
                  )}
                </Box>
              </Box>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Section title="Contact" action={editingSection !== 'contact' && <EditAction section="contact" />}>
                  {editingSection === 'contact' ? (
                    <>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12}>
                          <Box sx={{ maxWidth: 160 }}>
                            <ImageUpload
                              imageUrl={contactForm.profilePhoto}
                              onChange={(url) => setContactForm((prev) => ({ ...prev, profilePhoto: url }))}
                              onRemove={() => setContactForm((prev) => ({ ...prev, profilePhoto: '' }))}
                              setLoader={setPhotoUploading}
                              uploadLabel="Profile photo"
                              fileId="partner-profile-photo"
                              type="icon"
                              folderType={16}
                              disabled={photoUploading}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField sx={fieldSx} fullWidth label="Full name" value={contactForm.fullName} onChange={(e) => setContactForm((p) => ({ ...p, fullName: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField sx={fieldSx} fullWidth label="Email" type="email" value={contactForm.email} onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField sx={fieldSx} fullWidth label="Date of birth" type="date" InputLabelProps={{ shrink: true }} value={contactForm.dateOfBirth} onChange={(e) => setContactForm((p) => ({ ...p, dateOfBirth: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField sx={fieldSx} select fullWidth label="Partner type" value={contactForm.partnerType} onChange={(e) => setContactForm((p) => ({ ...p, partnerType: e.target.value }))}>
                            <MenuItem value="">None</MenuItem>
                            {PARTNER_TYPE_OPTIONS.map((t) => (
                              <MenuItem key={t} value={t}>
                                {partnerTypeLabel(t)}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      </Grid>
                      <SectionSaveActions onSave={saveContact} onCancel={cancelEdit} saving={sectionSaving} />
                    </>
                  ) : (
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Value label="Partner type" value={partnerTypeLabel(partner.partnerType)} />
                      </Grid>
                      <Grid item xs={6}>
                        <Value label="Onboarding step" value={pretty(partner.onboardingStep)} />
                      </Grid>
                      <Grid item xs={6}>
                        <Value label="Date of birth" value={formatDate(partner.dateOfBirth)} />
                      </Grid>
                      <Grid item xs={6}>
                        <Value label="Submitted" value={partner.submitted ? 'Yes' : 'No'} />
                      </Grid>
                    </Grid>
                  )}
                </Section>
              </Grid>

              <Grid item xs={12} md={6}>
                <Section title="Firm" action={editingSection !== 'firm' && <EditAction section="firm" />}>
                  {editingSection === 'firm' ? (
                    <>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <TextField sx={fieldSx} select fullWidth label="Business type" value={businessForm.businessType} onChange={(e) => setBusinessForm((p) => ({ ...p, businessType: e.target.value as BusinessFormState['businessType'] }))}>
                            {BUSINESS_TYPE_OPTIONS.map((o) => (
                              <MenuItem key={o.value} value={o.value}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={6}>
                          <TextField sx={fieldSx} fullWidth label="Legal Name" value={businessForm.legalName} onChange={(e) => setBusinessForm((p) => ({ ...p, legalName: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField sx={fieldSx} fullWidth label="Trading name" value={businessForm.tradingName} onChange={(e) => setBusinessForm((p) => ({ ...p, tradingName: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField sx={fieldSx} fullWidth label="GSTIN (optional)" value={businessForm.gstin} onChange={(e) => setBusinessForm((p) => ({ ...p, gstin: e.target.value }))} />
                        </Grid>
                        {businessForm.businessType === 'LIMITED_COMPANY' && (
                          <Grid item xs={6}>
                            <TextField sx={fieldSx} fullWidth label="CIN" value={businessForm.cin} onChange={(e) => setBusinessForm((p) => ({ ...p, cin: e.target.value }))} />
                          </Grid>
                        )}
                        {businessForm.businessType === 'SOLE_TRADER' && (
                          <Grid item xs={6}>
                            <TextField sx={fieldSx} fullWidth label="PAN" value={businessForm.pan} onChange={(e) => setBusinessForm((p) => ({ ...p, pan: e.target.value }))} />
                          </Grid>
                        )}
                      </Grid>
                      <SectionSaveActions onSave={saveFirm} onCancel={cancelEdit} saving={sectionSaving} />
                    </>
                  ) : (
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Value label="Business type" value={pretty(state.firm?.businessType)} />
                      </Grid>
                      <Grid item xs={6}>
                        <Value label="Legal Name" value={state.firm?.legalName} />
                      </Grid>
                      <Grid item xs={6}>
                        <Value label="Trading name" value={state.firm?.tradingName} />
                      </Grid>
                      <Grid item xs={6}>
                        <Value label={state.firm?.businessType === 'LIMITED_COMPANY' ? 'CIN' : 'PAN'} value={state.firm?.cin || (state.firm ? 'On file' : undefined)} />
                      </Grid>
                      <Grid item xs={6}>
                        <Value label="GSTIN" value={state.firm?.gstin} />
                      </Grid>
                    </Grid>
                  )}
                </Section>
              </Grid>

              <Grid item xs={12} md={6}>
                <Section title="Address" action={editingSection !== 'address' && <EditAction section="address" />}>
                  {editingSection === 'address' ? (
                    <>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12}>
                          <TextField sx={fieldSx} fullWidth label="Line 1" value={addressForm.line1} onChange={(e) => setAddressForm((p) => ({ ...p, line1: e.target.value }))} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField sx={fieldSx} fullWidth label="Line 2" value={addressForm.line2} onChange={(e) => setAddressForm((p) => ({ ...p, line2: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField sx={fieldSx} fullWidth label="City" value={addressForm.city} onChange={(e) => setAddressForm((p) => ({ ...p, city: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField sx={fieldSx} fullWidth label="State" value={addressForm.state} onChange={(e) => setAddressForm((p) => ({ ...p, state: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField sx={fieldSx} fullWidth label="PIN Code" value={addressForm.pincode} onChange={(e) => setAddressForm((p) => ({ ...p, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} />
                        </Grid>
                      </Grid>
                      <SectionSaveActions onSave={saveAddress} onCancel={cancelEdit} saving={sectionSaving} />
                    </>
                  ) : (
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <Value label="Line 1" value={state.address?.line1} />
                      </Grid>
                      <Grid item xs={12}>
                        <Value label="Line 2" value={state.address?.line2} />
                      </Grid>
                      <Grid item xs={6}>
                        <Value label="City" value={state.address?.city} />
                      </Grid>
                      <Grid item xs={6}>
                        <Value label="State" value={state.address?.state} />
                      </Grid>
                      <Grid item xs={6}>
                        <Value label="PIN Code" value={state.address?.pincode} />
                      </Grid>
                    </Grid>
                  )}
                </Section>
              </Grid>

              <Grid item xs={12} md={6}>
                <Section title="Bank & Identity" action={editingSection !== 'bank' && <EditAction section="bank" />}>
                  {editingSection === 'bank' ? (
                    <>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12}>
                          <TextField sx={fieldSx} fullWidth label="Account holder" value={bankForm.accountHolderName} onChange={(e) => setBankForm((p) => ({ ...p, accountHolderName: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField sx={fieldSx} fullWidth label="IFSC Code" value={bankForm.ifscCode} onChange={(e) => setBankForm((p) => ({ ...p, ifscCode: e.target.value.toUpperCase() }))} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField sx={fieldSx} fullWidth label="Account No." value={bankForm.accountNumber} onChange={(e) => setBankForm((p) => ({ ...p, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 18) }))} />
                        </Grid>
                      </Grid>
                      <Typography variant="caption" color="#64748b" sx={{ display: 'block', mt: 1 }}>
                        For security, the stored account number is never shown here — re-enter the full account number to save changes. Changing bank details re-runs the name-match verification.
                      </Typography>
                      <SectionSaveActions onSave={saveBank} onCancel={cancelEdit} saving={sectionSaving} />
                    </>
                  ) : (
                    <>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Value label="Account holder" value={state.bank?.accountHolderName} />
                        </Grid>
                        <Grid item xs={6}>
                          <Value label="IFSC Code" value={state.bank?.ifscCode} />
                        </Grid>
                        <Grid item xs={6}>
                          <Value label="Account No." value={state.bank ? 'On file (hidden for security)' : 'Not set'} />
                        </Grid>
                        <Grid item xs={6}>
                          <Value label="Verification" value={pretty(state.bank?.copResult)} />
                        </Grid>
                        <Grid item xs={6}>
                          <Value label="Verified on" value={state.bank?.copCheckedAt ? formatDate(state.bank.copCheckedAt) : 'N/A'} />
                        </Grid>
                        <Grid item xs={6}>
                          <Value label="Identity doc" value={pretty(state.identityDocument?.docType)} />
                        </Grid>
                      </Grid>
                      {state.identityDocument?.uploaded && (
                        <Button size="small" onClick={handleViewDocument} sx={{ color: ACCENT, mt: 1 }}>
                          View document
                        </Button>
                      )}
                    </>
                  )}
                </Section>
              </Grid>
            </Grid>

            <Section title="Verification Checks">
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Check', 'Result', 'Reason', 'Reviewed by', 'Checked at'].map((head) => (
                        <TableCell key={head} sx={{ color: '#64748b', borderColor: '#242424' }}>
                          {head}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.checks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ color: '#94a3b8', borderColor: '#242424' }}>
                          No checks yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      state.checks.map((check) => (
                        <TableRow key={check.checkType}>
                          <TableCell sx={{ color: '#e2e8f0', borderColor: '#242424' }}>{pretty(check.checkType)}</TableCell>
                          <TableCell sx={{ borderColor: '#242424' }}>
                            <CheckChip result={check.result} />
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderColor: '#242424' }}>{check.failureReason || 'N/A'}</TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderColor: '#242424' }}>{check.reviewedBy || 'N/A'}</TableCell>
                          <TableCell sx={{ color: '#94a3b8', borderColor: '#242424' }}>{formatDate(check.checkedAt)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
              <Divider sx={{ borderColor: '#242424', my: 1.5 }} />
              <Typography variant="caption" color="#64748b">
                Required checks: {state.requiredChecks.map(pretty).join(', ')}
              </Typography>
            </Section>

            <Section title="Agreements">
              <Box display="flex" gap={1} flexWrap="wrap">
                {state.agreements.length === 0 ? (
                  <Typography color="#94a3b8">No agreements accepted yet</Typography>
                ) : (
                  state.agreements.map((agreement) => (
                    <Chip
                      key={`${agreement.agreementType}-${agreement.agreementVersion}`}
                      label={`${pretty(agreement.agreementType)} · v${agreement.agreementVersion}`}
                      sx={{ bgcolor: '#242424', color: '#e2e8f0' }}
                    />
                  ))
                )}
              </Box>
            </Section>
          </>
        )}
      </Box>

      <Dialog
        open={Boolean(action)}
        onClose={() => !saving && setAction(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#171717', color: '#fff', border: '1px solid #242424', borderRadius: 3, backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {action === 'suspend' ? 'Suspend partner?' : 'Unsuspend partner?'}
          <IconButton onClick={() => !saving && setAction(null)} sx={{ color: '#94a3b8' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography color="#94a3b8">
            This will {action === 'suspend' ? 'pause this partner account until staff reactivates it.' : 'restore the partner status based on current verification checks.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" color={action === 'suspend' ? 'error' : 'success'} onClick={submitAction} disabled={saving}>
            {saving ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerDetail;
